import Router from 'vue-router';
import { ApplicationException } from '@pjn/core/error';
import { checkAccess } from '@pjn/core/security';
export const RouterView = { render(h) { return h('router-view'); } };
/**
 * Crea el router en base a la configuracion del router (o las rutas) y configuracion de seguridad.
 *
 * @param config Puede ser la lista de rutas a utilizar o, si se desea customizar la configuracion del router, la config completa (incluyendo las rutas).
 * @param security opcional. Configuracion de seguridad.
 *
 * @return el router configurado
 */
export function createRouter(config, security) {
    console.group('Vue Router');
    // Se determina si se paso por parametros la configuracion completa o solo la lista de rutas.
    const finalConfig = Array.isArray(config) ? { routes: config, mode: 'history' } : config;
    // Si no hay rutas, se debe fallar, ya que no tiene sentido.
    if (!finalConfig.routes)
        throw new Error('Se deben configurar rutas en el Router');
    console.debug('Configurando Router con la configuracion: %o', finalConfig);
    // Se crea el router
    const router = new Router(finalConfig);
    const publicHomeRoute = findPublicHome(finalConfig.routes);
    // Si hay configuracion de seguridad, se segurizan las rutas
    console.debug(security ? `Se incluye configuracion de seguridad: ${JSON.stringify({ authentication: security.authentication, privateHome: security.privateHome })}` : 'NO se implementa seguridad');
    if (security) {
        const privateHomeRoute = findPrivateHome(finalConfig.routes, security.privateHome);
        console.debug(privateHomeRoute ? `El Home PRIVADO es: ${privateHomeRoute}` : 'No hay Home PRIVADO, se usa siempre el publico');
        secureRouter(router, security.authStore, publicHomeRoute, privateHomeRoute);
    }
    console.groupEnd();
    return router;
}
/**
 * Se obtiene la ruta de home, buscando la que tenga path '/'.
 *
 * Esta ruta es obligatora, si no existe se tira excepcion.
 * @param routes configuracion de rutas
 * @throws ApplicationException si no hay home
 */
function findPublicHome(routes) {
    const route = routes.find(r => r.path === '/');
    if (!route)
        throw new ApplicationException('Se debe definir una ruta home (path "/") con nombre');
    console.debug('El Home PUBLICO es: %o', route);
    return route;
}
/**
 * Obtiene la ruta de home privada.
 * Para encontrarla se usa el nombre enviado por configuracion.
 *
 * Si no se especifica nombre, se asume que la ruta home es la misma que para usuarios anonimos.
 * @param routes configuracion de rutas
 * @param name opcional. Nombre de la ruta home privada
 */
function findPrivateHome(routes, name) {
    return name ? routes.find(r => r.name === name) : undefined;
}
/**
 * Seguriza las rutas del router utilizando interceptores.
 *
 * En concreto:
 *  - Valida el acceso a cada ruta y lo permite si:
 *    * La ruta es publica
 *    * La ruta es privada (sin roles) y el usuario esta autenticado (caso contrario lo envia al login).
 *    * La ruta es privada solo para ciertos roles y el usuario los tiene (caso contrario lo envia al home).
 *
 *  - Si se define home privado, se redirecciona a los usuarios autenticados del home publico al privado.
 *
 * @param router instancia del router a segurizar
 * @param authStore store que maneja la seguridad de la app
 * @param publicHomeRoute ruta home para usuarios anonimos
 * @param privateHomeRoute opcional. Ruta home para usuarios autenticados. Si no existe se usa la publica.
 */
export function secureRouter(router, authStore, publicHomeRoute, privateHomeRoute) {
    if (!authStore)
        throw new ApplicationException('Para utilizar seguridad debe proveer un AuthStore');
    if (privateHomeRoute) {
        const publicHomeInterceptor = publicHomeRoute.beforeEnter;
        // Si hay home privado, se interceptan las llamadas al publico y se redirecciona si esta autenticado
        // tslint:disable-next-line:only-arrow-functions
        publicHomeRoute.beforeEnter = function (_routeTo, _routeFrom, next) {
            if (authStore.authenticated) {
                console.debug('Como hay Home PRIVADO, se redirecionara al usuario a esa ruta');
                next({ path: privateHomeRoute.path, replace: true });
            }
            else if (publicHomeInterceptor) {
                // Si la ruta tiene definido otro interceptor, se lo llama.
                return publicHomeInterceptor(_routeTo, _routeFrom, next);
            }
            else {
                next();
            }
        };
    }
    router.beforeEach((routeTo, _routeFrom, next) => {
        // Comprueba si esta ruta requiere auth y roles (incluyendo rutas anidadas)
        const security = calculateRouteSecurity(routeTo);
        checkAccess(authStore, security, {
            onGranted() {
                // Se deja continuar
                next();
            },
            onLoginRequired() {
                // Se llama a login
                console.warn(`Pagina "${routeTo.name || routeTo.path}" no disponible para usuario anonimo, se intenta autenticacion`);
                next(false);
                authStore.login();
            },
            onRoleRequired(roles) {
                // Se redirige al home
                console.warn(`Pagina "${routeTo.name || routeTo.path}" requiere que el usuario tenga al menos uno de estos roles: ${roles}. Se redirige al home`);
                next({ path: publicHomeRoute.path });
            }
        });
    });
}
/**
 * Calcula la seguridad de una ruta (anidada). La logica es:
 *  - Se usan los roles de la ruta mas especifica que los incluya.
 *  - Si ninguna ruta incluye roles, con que una este segurizada alcanza.
 *  - Si ninguna esta segurizada, la ruta completa no lo esta tampoco.
 *
 * @param route ruta para la cual se quiere calcular la seguridad.
 */
export function calculateRouteSecurity(route) {
    // Empieza no segurizada
    let result = false;
    route.matched.forEach((route) => {
        const routeSecurity = route.meta.auth;
        if (Array.isArray(routeSecurity)) {
            // Si es lista de roles la ruta, sobreescribe el resultado
            result = routeSecurity;
        }
        else if (Array.isArray(result)) {
            // Si no es lista de roles y ya se encontro una lista, se deja la anterior
            return;
        }
        // Si ninguna es lista de roles, con que una sea true, el resultado debe serlo
        result = routeSecurity || result;
    });
    return result;
}
//# sourceMappingURL=router.js.map